import {
  WEB_AGENT_STEP_ACTIONS,
  type WebAgentBrowserDataType,
  type WebAgentStep,
  type WebAgentStepAction,
} from '@circuitry-ai/doc-data';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { Checkbox, Chip, Input, Select, SelectItem, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { type Key, type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isCredentialValue, WebAgentValueField } from './WebAgentValueField';

const NEEDS_TARGET = new Set<WebAgentStepAction>(['click', 'fill', 'uploadFile', 'select', 'captureHtml', 'waitFor', 'store']);
const NEEDS_VALUE = new Set<WebAgentStepAction>(['goto', 'fill', 'uploadFile', 'press', 'select', 'waitFor', 'switchTab', 'forEach']);
const OPENS_NEW_TAB = new Set<WebAgentStepAction>(['click', 'select', 'press']);
// Actions whose value is data the page receives, so it may be a stored secret or a per-run value.
// waitFor seconds: max wait when a selector is set (early exit), or fixed sleep with no selector.
// switchTab (a tab index) is neither secret nor run-value, and keeps a plain input.
const VALUE_FROM_SOURCES = new Set<WebAgentStepAction>(['goto', 'fill', 'uploadFile', 'press', 'select']);
const VALUE_LABEL: Partial<Record<WebAgentStepAction, string>> = { waitFor: 'Seconds', switchTab: 'Tab (index or "last")', forEach: 'List' };
const VALUE_PLACEHOLDER: Partial<Record<WebAgentStepAction, string>> = {
  uploadFile: 'Attachment field',
  waitFor: 'Max seconds (with selector) or sleep',
  switchTab: 'last',
  press: 'Enter',
  forEach: '${Repairs}',
};
const NUMERIC_VALUE = new Set<WebAgentStepAction>(['waitFor']);
const toSeconds = (raw: string) => {
  const digits = raw.replace(/\D/g, '');
  return digits === '' ? '' : Number(digits);
};

type ValueType = 'secret' | 'text' | 'runValue';
const VALUE_TYPES: ReadonlyArray<{ key: ValueType; label: string }> = [
  { key: 'secret', label: 'Secret' },
  { key: 'text', label: 'Text' },
  { key: 'runValue', label: 'Run value' },
];

/** A `${...}` placeholder the runner fills in per run: a command input, or the agent's start URL. */
const RUN_VALUE_RE = /\$\{[^}]+\}/;
/** A `{name}` placeholder for a value stored at runtime by a preceding `store` step. */
const STORED_VAR_RE = /^\{[a-zA-Z_]\w*\}$/;

/** Both placeholder flavours are picked from the Run value dropdown rather than typed. */
const isRunValue = (value: WebAgentStep['value']): value is string =>
  typeof value === 'string' && (RUN_VALUE_RE.test(value) || STORED_VAR_RE.test(value));

const valueTypeOf = (step: WebAgentStep): ValueType => {
  if (isCredentialValue(step.value)) return 'secret';
  if (isRunValue(step.value)) return 'runValue';
  if (step.action === 'uploadFile') return 'runValue';
  if (step.action === 'fill' && (step.value === undefined || step.value === '')) return 'secret';
  return 'text';
};

// NextUI's outside-left recipe for Select ends with `items-start`, overriding the `items-center` in the
// same rule, and never stretches the trigger. Inputs do both, so without this a select's label sits
// higher and its control narrower than the fields beside it.
const SELECT_OUTSIDE_LEFT = { base: 'items-center', mainWrapper: 'flex-1' };
const SCROLLABLE_SELECT_CLASSNAMES = {
  popoverContent: 'overflow-x-auto',
  listbox: 'min-w-max',
} as const;
const BROWSER_DATA_TYPE_OPTIONS: ReadonlyArray<{ key: WebAgentBrowserDataType; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'cookies', label: 'Cookies' },
  { key: 'localStorage', label: 'Local storage' },
  { key: 'sessionStorage', label: 'Session storage' },
];

const exclusiveAllSelection = <T extends string>(keys: 'all' | Set<Key>, current: T[]): T[] => {
  const selected = keys === 'all' ? ['all'] : [...keys].map(String);
  if (!selected.includes('all')) return selected as T[];
  if (current.includes('all' as T) && selected.length > 1) return selected.filter((key) => key !== 'all') as T[];
  return ['all'] as T[];
};

function BrowserDataFieldsInput({ values, isReadOnly, onChange }: Readonly<{
  values: string[];
  isReadOnly?: boolean;
  onChange: (values: string[]) => void;
}>) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const commit = () => {
    const next = inputValue.split(',').map((value) => value.trim()).filter(Boolean);
    if (!next.length) return;
    onChange([...new Set([...values, ...next])]);
    setInputValue('');
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Backspace' && !inputValue && values.length) {
      onChange(values.slice(0, -1));
    }
  };
  return (
    <div className="flex min-h-10 flex-1 flex-wrap items-center gap-1.5 rounded-medium border border-default-200 bg-transparent px-2">
      {values.map((value) => (
        <Chip key={value} size="sm" variant="flat" radius="sm" onClose={isReadOnly ? undefined : () => onChange(values.filter((item) => item !== value))}>
          {value}
        </Chip>
      ))}
      <input
        className="min-w-[120px] flex-1 bg-transparent text-sm text-foreground outline-none disabled:cursor-not-allowed"
        value={inputValue}
        onChange={(event) => setInputValue(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={values.length ? '' : t('Optional: enter cookie or storage keys')}
        disabled={isReadOnly}
        aria-label="browser_data_fields"
      />
    </div>
  );
}

export type StepRowProps = Readonly<{
  step: WebAgentStep;
  index: number;
  total: number;
  isReadOnly?: boolean;
  /** Command form input names, offered in the value dropdown as `${name}`. */
  availableInputs?: string[];
  /** Whether the agent has a start URL, so `${startUrl}` is worth offering. */
  hasStartUrl?: boolean;
  /** Earlier step ids offered for skipWhenStepId (steps above this row). */
  priorStepIds?: string[];
  /** Variable names captured by `store` steps that appear before this row, offered as `{name}`. */
  storedVarNames?: string[];
  onStep: (partial: Partial<WebAgentStep>) => void;
  onTarget: (partial: Partial<NonNullable<WebAgentStep['target']>>) => void;
  onMove?: (dir: -1 | 1) => void;
  onRemove?: () => void;
  /** A step inside a forEach. Hides the session-level options the repeated steps do not use. */
  nested?: boolean;
  /** Other forEach steps this step can follow. */
  repeatableSteps?: ReadonlyArray<{ id: string; label: string }>;
}>;

export function StepRow({ step, index, total, isReadOnly, availableInputs, hasStartUrl, priorStepIds = [], storedVarNames = [], onStep, onTarget, onMove, onRemove, nested, repeatableSteps = [] }: StepRowProps) {
  const { t } = useTranslation();

  const [valueType, setValueType] = useState<ValueType>(() => valueTypeOf(step));
  const lastEmitted = useRef(step.value);

  useEffect(() => {
    // Only a change from outside this row (JSON view, assistant applying steps) re-reads the type. The
    // user's own edits must not, or clearing a fill would jump back to the credential picker.
    if (step.value === lastEmitted.current) return;
    lastEmitted.current = step.value;
    setValueType(valueTypeOf(step));
  }, [step]);

  const setValue = (next: string | number) => {
    lastEmitted.current = next;
    onStep({ value: next });
  };

  const changeValueType = (next: ValueType) => {
    setValueType(next);
    if (step.value !== undefined && step.value !== '' && valueTypeOf(step) !== next) setValue('');
  };

  /** `withLabel` is false when the row already shows one shared Value label beside the type dropdown,
   *  which is what keeps every type's control the same width. */
  const valueInput = (withLabel: boolean) => (
    <Input
      size="sm" aria-label="step_value" className="w-full" isReadOnly={isReadOnly}
      {...(withLabel ? { label: t(VALUE_LABEL[step.action] ?? 'Value'), labelPlacement: 'outside-left' as const } : {})}
      value={String(step.value ?? '')} placeholder={t(VALUE_PLACEHOLDER[step.action] ?? 'Enter value')}
      inputMode={NUMERIC_VALUE.has(step.action) ? 'numeric' : undefined}
      onValueChange={(v) => setValue(NUMERIC_VALUE.has(step.action) ? toSeconds(v) : v)}
    />
  );

  const renderValueField = () => {
    if (valueType === 'text') return valueInput(false);
    if (valueType === 'secret') {
      return (
        <WebAgentValueField
          value={String(step.value ?? '')}
          placeholder={t('Pick a credential')}
          isReadOnly={isReadOnly}
          onChange={setValue}
        />
      );
    }
    const selectedRunValue = isRunValue(step.value) ? step.value : '';
    // Stored runtime vars from preceding `store` steps show as `{name}`; command/URL inputs as `${name}`.
    const runValues = [
      ...storedVarNames.map((name) => `{${name}}`),
      ...[...(hasStartUrl ? ['startUrl'] : []), ...(availableInputs ?? [])].map((name) => `\${${name}}`),
    ];
    // A value naming an input that no longer exists still belongs in the list, or it would vanish from
    // the editor while staying in the saved config.
    if (selectedRunValue && !runValues.includes(selectedRunValue)) runValues.push(selectedRunValue);
    return (
      <Select
        aria-label="step_value" size="sm" className="w-full" isDisabled={isReadOnly}
        placeholder={runValues.length ? t('Pick a run value') : t('No run values configured')}
        selectedKeys={selectedRunValue ? new Set([selectedRunValue]) : new Set<string>()}
        onSelectionChange={(keys) => setValue(String([...keys][0] ?? ''))}
      >
        {runValues.map((template) => <SelectItem key={template} value={template}>{template}</SelectItem>)}
      </Select>
    );
  };

  return (
    <div className="flex flex-col gap-2 border border-default-200 rounded-md p-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-default-400 min-w-[3.5rem]">{step.id || `step-${index + 1}`}</span>
        <Select
          aria-label="step_action" size="sm" className="w-36" selectedKeys={new Set([step.action])} disallowEmptySelection isDisabled={isReadOnly}
          classNames={SCROLLABLE_SELECT_CLASSNAMES}
          onSelectionChange={(keys) => {
            const action = String([...keys][0]) as WebAgentStepAction;
            onStep({
              action,
              ...(OPENS_NEW_TAB.has(action) ? {} : { opensNewTab: undefined }),
              ...(action === 'click' ? {} : { downloadsFile: undefined }),
            });
          }}
        >
          {WEB_AGENT_STEP_ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
        </Select>
        <div className="flex-1" />
        {!nested && <Checkbox isSelected={Boolean(step.optional)} isDisabled={isReadOnly} onValueChange={(v) => onStep({ optional: v })} size="sm">{t('Optional')}</Checkbox>}
        {!nested && (
          <Tooltip content={t('Run this only on the first message of a conversation. The browser stays open between messages, so a sign-in does not need repeating.')} placement="top">
            <span>
              <Checkbox isSelected={Boolean(step.skipOnRepeat)} isDisabled={isReadOnly} onValueChange={(v) => onStep({ skipOnRepeat: v })} size="sm">{t('Skip on repeat')}</Checkbox>
            </span>
          </Tooltip>
        )}
        {!isReadOnly && (onMove || onRemove) && (
          <>
            {onMove && <CtyButton isIconOnly size="sm" variant="light" aria-label="move_up" isDisabled={index === 0} onPress={() => onMove(-1)}><Icon icon="mdi:arrow-up" /></CtyButton>}
            {onMove && <CtyButton isIconOnly size="sm" variant="light" aria-label="move_down" isDisabled={index === total - 1} onPress={() => onMove(1)}><Icon icon="mdi:arrow-down" /></CtyButton>}
            {onRemove && <CtyButton isIconOnly size="sm" variant="light" color="danger" aria-label="remove_step" onPress={onRemove}><Icon icon="mdi:trash-can-outline" /></CtyButton>}
          </>
        )}
      </div>

      {step.action === 'forEach' && (
        <div className="flex flex-col gap-2">
          <Input
            size="sm" aria-label="foreach_row" label={t('Row selector')} labelPlacement="outside-left"
            className="w-full" isReadOnly={isReadOnly} placeholder="#cccTable tr.cccRow"
            value={step.rowSelector ?? ''} onValueChange={(v) => onStep({ rowSelector: v || undefined })}
          />
          <Input
            size="sm" aria-label="foreach_add" label={t('Add button selector')} labelPlacement="outside-left"
            className="w-full" isReadOnly={isReadOnly} placeholder={'button[onclick="addRow()"]'}
            value={step.addSelector ?? ''} onValueChange={(v) => onStep({ addSelector: v || undefined })}
          />
          <Select
            aria-label="foreach_depends" size="sm" className="w-full"
            label={t('Depends on')} labelPlacement="outside-left"
            classNames={SELECT_OUTSIDE_LEFT}
            placeholder={t('None')}
            isDisabled={isReadOnly}
            selectedKeys={step.dependsOnStepId ? new Set([step.dependsOnStepId]) : new Set(['__none__'])}
            onSelectionChange={(keys) => {
              const raw = String([...keys][0] ?? '');
              onStep({ dependsOnStepId: !raw || raw === '__none__' ? undefined : raw });
            }}
          >
            <SelectItem key="__none__" value="__none__">{t('None')}</SelectItem>
            {repeatableSteps.filter((item) => item.id !== step.id).map((item) => (
              <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>
            ))}
          </Select>
        </div>
      )}

      {NEEDS_TARGET.has(step.action) && (
        <div className="flex gap-2">
          <Input size="sm" aria-label="target_selector" className="flex-1" label={t('Selector')} labelPlacement="outside-left" value={step.target?.selector ?? ''} isReadOnly={isReadOnly} placeholder="#css / text= || #fallback" onValueChange={(v) => onTarget({ selector: v })} />
          {step.action !== 'store' && (
            <>
              <Input size="sm" aria-label="target_role" className="w-40" label={t('Role')} labelPlacement="outside-left" value={step.target?.role ?? ''} isReadOnly={isReadOnly} placeholder="button" onValueChange={(v) => onTarget({ role: v })} />
              <Input size="sm" aria-label="target_name" className="w-48" label={t('Name')} labelPlacement="outside-left" value={step.target?.name ?? ''} isReadOnly={isReadOnly} placeholder="Login" onValueChange={(v) => onTarget({ name: v })} />
            </>
          )}
        </div>
      )}

      {step.action === 'store' && (
        <div className="flex flex-wrap gap-2 items-start">
          <Input
            size="sm" aria-label="store_as" className="w-full max-w-xs"
            label={t('As (variable name)')} labelPlacement="outside-left"
            isRequired isReadOnly={isReadOnly}
            value={step.as ?? ''}
            placeholder={t('e.g. producttype')}
            description={`${t('Later steps can use this value as')} {${step.as || 'variablename'}}`}
            onValueChange={(v) => onStep({ as: v.replace(/\s+/g, '') || undefined })}
          />
          <Select
            aria-label="store_split" size="sm" className="w-40"
            label={t('Split')} labelPlacement="outside-left"
            classNames={SELECT_OUTSIDE_LEFT}
            placeholder={t('None')}
            isDisabled={isReadOnly}
            selectedKeys={step.split ? new Set([step.split]) : new Set(['__none__'])}
            onSelectionChange={(keys) => {
              const raw = String([...keys][0] ?? '');
              onStep({ split: raw === 'first' || raw === 'last' ? raw : undefined });
            }}
          >
            <SelectItem key="__none__" value="__none__">{t('None')}</SelectItem>
            <SelectItem key="first" value="first">{t('first')}</SelectItem>
            <SelectItem key="last" value="last">{t('last')}</SelectItem>
          </Select>
        </div>
      )}

      <div className="flex gap-2 items-center">
        {NEEDS_VALUE.has(step.action) && (
          VALUE_FROM_SOURCES.has(step.action) ? (
            <>
              <Select
                aria-label="value_type" size="sm" className="w-44 shrink-0" label={t('Type')} labelPlacement="outside-left"
                classNames={SELECT_OUTSIDE_LEFT} selectedKeys={new Set([valueType])} disallowEmptySelection isDisabled={isReadOnly}
                onSelectionChange={(keys) => changeValueType(String([...keys][0]) as ValueType)}
              >
                {VALUE_TYPES.map(({ key, label }) => <SelectItem key={key} value={key}>{t(label)}</SelectItem>)}
              </Select>
              <span className="text-small text-foreground shrink-0">{t('Value')}</span>
              <div className="flex-1 min-w-0">
                {renderValueField()}
              </div>
            </>
          ) : step.action === 'waitFor' ? (
            <Tooltip
              content={t('With a selector: wait up to this many seconds and continue as soon as it appears. Without a selector: sleep this many seconds.')}
              placement="top"
            >
              <div className="flex-1 min-w-0">{valueInput(true)}</div>
            </Tooltip>
          ) : valueInput(true)
        )}
        {step.action === 'fill' && (
          <Checkbox isSelected={Boolean(step.submit)} isDisabled={isReadOnly} onValueChange={(v) => onStep({ submit: v })} size="sm">{t('Submit (press Enter)')}</Checkbox>
        )}
        {OPENS_NEW_TAB.has(step.action) && (
          <Tooltip
            content={t('Check when this opens a new browser tab. The next switch to the last tab then waits up to 30 seconds for it. Leave off for a tab inside the page; that switch uses the browser tab already open.')}
            placement="top"
          >
            <span>
              <Checkbox
                isSelected={Boolean(step.opensNewTab)}
                isDisabled={isReadOnly}
                onValueChange={(v) => onStep({ opensNewTab: v || undefined })}
                size="sm"
              >
                {t('Opens a new browser tab')}
              </Checkbox>
            </span>
          </Tooltip>
        )}
        {step.action === 'click' && (
          <Tooltip
            content={t('Check when this click downloads a file. The file is saved to S3 with the other attachments of the run.')}
            placement="top"
          >
            <span>
              <Checkbox
                isSelected={Boolean(step.downloadsFile)}
                isDisabled={isReadOnly}
                onValueChange={(v) => onStep({ downloadsFile: v || undefined })}
                size="sm"
              >
                {t('Downloads a file')}
              </Checkbox>
            </span>
          </Tooltip>
        )}
      </div>

      {step.action === 'uploadFile' && (
        <div className="flex flex-col gap-2">
          <Input
            size="sm"
            aria-label="upload_accept"
            className="w-full"
            label={t('Allowed file types')}
            labelPlacement="outside-left"
            isReadOnly={isReadOnly}
            placeholder="pdf, jpg, png"
            value={(step.accept ?? []).join(', ')}
            onValueChange={(v) => {
              const accept = v.split(',').map((item) => item.trim().replace(/^\./, '').toLowerCase()).filter(Boolean);
              onStep({ accept: accept.length ? accept : undefined });
            }}
          />
          <Input
            size="sm"
            aria-label="upload_max_size"
            className="w-full"
            label={t('Maximum file size (MB)')}
            labelPlacement="outside-left"
            isReadOnly={isReadOnly}
            placeholder={t('No limit')}
            value={step.maxSizeMb != null ? String(step.maxSizeMb) : ''}
            onValueChange={(v) => {
              const size = Number(v);
              onStep({ maxSizeMb: v.trim() && Number.isFinite(size) && size > 0 ? size : undefined });
            }}
          />
          <Checkbox
            isSelected={step.multiple === true}
            isDisabled={isReadOnly}
            onValueChange={(v) => onStep({ multiple: v || undefined })}
            size="sm"
          >
            {t('Allow multiple files')}
          </Checkbox>
        </div>
      )}

      {step.action === 'captureHtml' && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-3 items-center">
            <Tooltip content={t('When on, linked files under the capture target are downloaded during this step. Off by default.')} placement="top">
              <span>
                <Checkbox
                  isSelected={Boolean(step.downloadAttachments)}
                  isDisabled={isReadOnly}
                  onValueChange={(v) => onStep({
                    downloadAttachments: v || undefined,
                    ...(v ? {} : { attachmentFilterSelector: undefined, attachmentFilterValue: undefined }),
                  })}
                  size="sm"
                >
                  {t('Download attachments')}
                </Checkbox>
              </span>
            </Tooltip>
            <Tooltip content={t('When on, capture each page using the next-page selector until the last page. Leave the selector empty to capture page 1 only.')} placement="top">
              <span>
                <Checkbox
                  isSelected={Boolean(step.traversePages)}
                  isDisabled={isReadOnly}
                  onValueChange={(v) => onStep({
                    traversePages: v,
                    ...(v ? {} : { nextPageSelector: undefined, maxPages: undefined }),
                  })}
                  size="sm"
                >
                  {t('Traverse all table pages')}
                </Checkbox>
              </span>
            </Tooltip>
          </div>
          {step.downloadAttachments && (
            <div className="flex flex-wrap gap-2 items-start">
              <Input
                size="sm"
                aria-label="attachment_filter_selector"
                className="flex-1 min-w-[12rem]"
                label={t('Attachment filter selector')}
                labelPlacement="outside-left"
                value={step.attachmentFilterSelector ?? ''}
                isReadOnly={isReadOnly}
                placeholder={t('Optional — td:nth-of-type(2) || .doc-type')}
                onValueChange={(v) => onStep({ attachmentFilterSelector: v.trim() || undefined })}
              />
              <Input
                size="sm"
                aria-label="attachment_filter_value"
                className="flex-1 min-w-[12rem]"
                label={t('Attachment filter value')}
                labelPlacement="outside-left"
                value={step.attachmentFilterValue ?? ''}
                isReadOnly={isReadOnly}
                placeholder={t('Exact text — Parts Invoice || Work Order')}
                onValueChange={(v) => onStep({ attachmentFilterValue: v.trim() || undefined })}
              />
            </div>
          )}
          {step.traversePages && (
            <Input
              size="sm"
              aria-label="next_page_selector"
              className="w-full"
              label={t('Next page selector')}
              labelPlacement="outside-left"
              value={step.nextPageSelector ?? ''}
              isReadOnly={isReadOnly}
              placeholder={t('Leave empty to capture page 1 only')}
              onValueChange={(v) => onStep({ nextPageSelector: v || undefined })}
            />
          )}
          <Input
            size="sm"
            aria-label="extract_columns"
            className="w-full"
            label={t('Extract columns')}
            labelPlacement="outside-left"
            value={Array.isArray(step.extractColumns) ? step.extractColumns.join(', ') : (step.extractColumns ?? '')}
            isReadOnly={isReadOnly}
            placeholder={t('Claim # (comma-separated; leave empty for full capture)')}
            onValueChange={(v) => {
              const cols = v.split(',').map((c) => c.trim()).filter(Boolean);
              onStep({ extractColumns: cols.length ? cols : undefined });
            }}
          />
        </div>
      )}

      {step.action === 'captureBrowserData' && (
        <div className="flex flex-col gap-2">
          <Select
            aria-label="browser_data_types"
            size="sm"
            className="w-full"
            label={t('Browser data')}
            labelPlacement="outside-left"
            selectionMode="multiple"
            disallowEmptySelection
            isDisabled={isReadOnly}
            selectedKeys={new Set(step.browserDataTypes?.length ? step.browserDataTypes : ['all'])}
            onSelectionChange={(keys) => {
              const current: WebAgentBrowserDataType[] = step.browserDataTypes?.length ? step.browserDataTypes : ['all'];
              onStep({ browserDataTypes: exclusiveAllSelection<WebAgentBrowserDataType>(keys, current) });
            }}
          >
            {BROWSER_DATA_TYPE_OPTIONS.map(({ key, label }) => (
              <SelectItem key={key} value={key}>{t(label)}</SelectItem>
            ))}
          </Select>
          <div className="flex items-start gap-2">
            <span className="min-w-fit pt-2 text-small text-foreground">{t('Fields')}</span>
            <BrowserDataFieldsInput
              values={step.browserDataFields ?? []}
              isReadOnly={isReadOnly}
              onChange={(values) => onStep({ browserDataFields: values.length ? values : undefined })}
            />
          </div>
          <p className="text-xs text-default-500">
            {t('Optional. Leave empty to capture every available key; press Enter or comma to add a field. Cookie capture includes HttpOnly cookies for the current page.')}
          </p>
        </div>
      )}

      {step.action === 'forEach' && (
        <div className="flex flex-col gap-2 rounded-md border border-divider bg-content2 p-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-foreground">{t('Repeated steps')}</span>
            {!isReadOnly && (
              <CtyButton size="sm" variant="light" onPress={() => onStep({
                nestedSteps: [...(step.nestedSteps ?? []), { action: 'click', value: '', target: { selector: '' } }],
              })}
              >
                {t('Add step')}
              </CtyButton>
            )}
          </div>
          <span className="text-xs text-default-500">
            {t('These run once for each item. ${item.Field} is that item\'s field and ${index} is 0, 1, 2…')}
          </span>
          {(step.nestedSteps ?? []).map((child, childIndex, all) => (
            <StepRow
              key={`${step.id ?? index}-nested-${childIndex}`}
              nested
              step={child}
              index={childIndex}
              total={all.length}
              isReadOnly={isReadOnly}
              availableInputs={availableInputs}
              hasStartUrl={hasStartUrl}
              storedVarNames={storedVarNames}
              onStep={(partial) => onStep({
                nestedSteps: (step.nestedSteps ?? []).map((item, i) => (i === childIndex ? { ...item, ...partial } : item)),
              })}
              onTarget={(partial) => onStep({
                nestedSteps: (step.nestedSteps ?? []).map((item, i) => (
                  i === childIndex ? { ...item, target: { ...item.target, ...partial } } : item
                )),
              })}
              onMove={isReadOnly ? undefined : (dir) => {
                const next = [...(step.nestedSteps ?? [])];
                const j = childIndex + dir;
                if (j < 0 || j >= next.length) return;
                [next[childIndex], next[j]] = [next[j], next[childIndex]];
                onStep({ nestedSteps: next });
              }}
              onRemove={isReadOnly ? undefined : () => onStep({
                nestedSteps: (step.nestedSteps ?? []).filter((_, i) => i !== childIndex),
              })}
            />
          ))}
        </div>
      )}

      {!nested && priorStepIds.length > 0 && (
        <Tooltip
          content={t('Skip this step when the selected earlier step succeeds. For captureHtml, skip only if that capture produced non-empty JSON.')}
          placement="top"
        >
          <div className="w-full max-w-md">
            <Select
              aria-label="skip_when_step"
              size="sm"
              className="w-full"
              label={t('Skip when step succeeds')}
              labelPlacement="outside-left"
              classNames={SELECT_OUTSIDE_LEFT}
              placeholder={t('None')}
              isDisabled={isReadOnly}
              selectedKeys={step.skipWhenStepId ? new Set([step.skipWhenStepId]) : new Set(['__none__'])}
              onSelectionChange={(keys) => {
                const raw = String([...keys][0] ?? '');
                onStep({ skipWhenStepId: !raw || raw === '__none__' ? undefined : raw });
              }}
            >
              <SelectItem key="__none__" value="__none__">{t('None')}</SelectItem>
              {priorStepIds.map((id) => (
                <SelectItem key={id} value={id}>{id}</SelectItem>
              ))}
            </Select>
          </div>
        </Tooltip>
      )}
    </div>
  );
}

export default StepRow;
